<section class="bg-star-gray-600 pt-[40px] pb-[56px] lg:pt-[64px] lg:pb-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px] text-center">
    <p class="text-[13px] uppercase tracking-[1.6px] text-star-yellow-500">
      Solutions for OTC desks
    </p>
    <h1
      class="mt-[14px] text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[42px]"
    >
      AML Screening for OTC Desks and Brokers
    </h1>
    <p class="mx-auto mt-[20px] max-w-[720px] text-[16px] leading-[1.8] text-white/80">
      An OTC desk does not process a stream of small payments. It settles a handful of very large
      trades, each with a counterparty it may deal with once. AMLOfficer is the check that fits
      between the quote and the settlement: one address, read through four AML data providers, with
      an answer fast enough to still be part of the deal.
    </p>
    <div class="mt-[28px] flex flex-col items-center gap-[16px]">
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
      <p class="max-w-[540px] text-[14px] leading-[1.6] text-white/50">
        Desks work from a business account, and commercial terms follow the volume you actually put
        through it — the number appears in the account once you register.
      </p>
    </div>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      One trade, one chance to look
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      The recurring problem on a desk is not fraud detection in the abstract. It is that the source
      of a counterparty's funds is opaque at the exact moment the size of the trade makes it matter
      most. A desk rarely has a relationship history to fall back on, the window between agreeing
      the price and moving the funds is short, and once settlement happens there is no unwinding it.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      That makes a desk's compliance work concentrated rather than continuous. Very few decisions,
      each one heavy. What helps is not automation of a pipeline — it is a check an analyst or a
      trader can run in the moment, on the one address the deal turns on, and a record of it
      afterwards.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Where the check sits in a deal
    </h2>
    <ol
      class="mt-[32px] flex flex-col border-l border-solid border-white/15 pl-[24px] lg:pl-[32px]"
    >
      <li class="relative pb-[32px]">
        <span
          class="absolute left-[-30px] top-[4px] h-[12px] w-[12px] rounded-full border-2 border-solid border-star-yellow-500 bg-star-gray-600 lg:left-[-38px]"
        ></span>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">Before you quote</h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Screening the counterparty's address while the trade is still a conversation is the
          cheapest point in the whole process. Nothing has been promised, and the desk still has
          every option — including declining without an explanation it does not want to give.
        </p>
      </li>
      <li class="relative pb-[32px]">
        <span
          class="absolute left-[-30px] top-[4px] h-[12px] w-[12px] rounded-full border-2 border-solid border-star-yellow-500 bg-star-gray-600 lg:left-[-38px]"
        ></span>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">Before the funds move</h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          The address that will actually send or receive is not always the address discussed
          earlier. Re-checking the settlement address is what turns pre-trade due diligence into
          something that describes the trade you are really doing.
        </p>
      </li>
      <li class="relative pb-[32px]">
        <span
          class="absolute left-[-30px] top-[4px] h-[12px] w-[12px] rounded-full border-2 border-solid border-star-yellow-500 bg-star-gray-600 lg:left-[-38px]"
        ></span>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">After settlement</h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          A counterparty you expect to see again is worth keeping under monitoring: the address is
          added once, and a change in what it is exposed to arrives as an alert rather than as a
          surprise during the next negotiation.
        </p>
      </li>
      <li class="relative">
        <span
          class="absolute left-[-30px] top-[4px] h-[12px] w-[12px] rounded-full border-2 border-solid border-star-yellow-500 bg-star-gray-600 lg:left-[-38px]"
        ></span>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">When someone asks later</h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Reports stay attached to the address, so the desk can show what it knew and when it knew
          it — the part of due diligence that only matters once, and matters completely.
        </p>
      </li>
    </ol>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Four opinions on an address, not one
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer aggregates four AML providers, including BitOK, Global Ledger and Beosin. The point
      of aggregation is disagreement: providers draw on different datasets, and an address one of
      them calls clean is regularly flagged by another.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      On a desk that argument needs no elaboration. When a single trade can be worth more than a
      month of ordinary flow, a second opinion on the counterparty's address is not redundancy — it
      is the difference between a decision and a guess. Where providers disagree, the desk sees the
      disagreement rather than an averaged verdict.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Run the check where the deal is happening
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Desks close trades on the phone, in a chat, at an airport. The tooling has to accept that.
      Wallet reports and alerts work from Telegram, so a trader can request a check and read the
      answer without opening anything else, while the analyst behind them works the same case in the
      dashboard with the full report in front of them.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Desks that book trades through their own system can go further and call the API instead, so
      the check is triggered by the deal ticket rather than by someone remembering. Manual and
      integrated use are the same product; a desk usually starts with the first and adds the second
      when volume justifies it.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Either way the work is shared: business accounts support role-based access and shared
      investigations, which is what lets a trader raise a question and a compliance officer answer
      it on the same case.
    </p>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      OTC compliance: common questions
    </h2>
    <div class="mt-[28px] flex flex-col gap-[24px]">
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Can a trader run a check without waiting for the compliance team?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Yes. Checks run from the dashboard or from Telegram, and the report is shared with the
          rest of the team through the account — so speed on the desk does not cost you the record
          afterwards.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Do we need an integration to start?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          No. The manual tools in the web interface are the whole product for most desks. The API is
          there for desks that want checks triggered from their own booking system.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          What does a check tell us about the counterparty?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          It describes the address, not the person: where its money has been and what it has been
          close to. Who the counterparty is remains your own due diligence — this is the part of it
          that the chain can answer.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Can we keep watching a counterparty between trades?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Yes. Add the address to monitoring and choose the depth you need — deposits, withdrawals,
          or its full history — and alerts on it reach the dashboard and your Telegram group.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          What does it cost for a desk?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Commercial terms scale with volume and appear in the account after registration. A desk
          that wants shared access and monitoring works from a business account.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pt-[56px] pb-[72px] lg:pt-[72px] lg:pb-[96px]">
  <div class="mx-auto max-w-[860px] px-[20px]">
    <div
      class="rounded-[20px] bg-star-gray-650 px-[24px] py-[36px] shadow-inner-white lg:px-[48px] lg:py-[48px]"
    >
      <h2 class="max-w-[620px] text-[24px] font-medium leading-[1.3] text-white lg:text-[30px]">
        Check the next counterparty before you settle
      </h2>
      <p class="mt-[16px] max-w-[620px] text-[16px] leading-[1.75] text-white/70">
        Open a business account to screen counterparty addresses, keep the ones you deal with
        regularly under monitoring, and give your desk and your compliance officer the same case to
        work on.
      </p>
      <div class="mt-[28px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <a
          [routerLink]="'/contact-us'"
          class="text-[16px] leading-[1.6] text-white/70 underline underline-offset-4 hover:text-white"
        >
          Or speak to the team first
        </a>
      </div>
    </div>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
